import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import StatCard from "../components/StatCard";
import API from "../api/axios";
import {
  LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  MdDevices, MdCheckCircle,
  MdError, MdSpeed,
} from "react-icons/md";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    avgLatency: 0,
  });
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [bandwidthData, setBandwidthData] = useState([]);
  const [latencyData, setLatencyData] = useState([]);
    const [deviceTypeData, setDeviceTypeData] = useState([]);
  const [alertSeverityData, setAlertSeverityData] = useState([]);
  const [networkTrafficData, setNetworkTrafficData] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchAlerts();
    fetchLatencyData();
    fetchBandwidthData();
    fetchDeviceTypeData();
    fetchAlertSeverityData();
    fetchNetworkTrafficData();
  }, []);

  const fetchLatencyData = async () => {
    try {
      const res = await API.get("/devices");
      
      // Process latency data from devices
      const deviceLatency = res.data
        .filter(device => device.latency !== null && device.latency !== undefined)
        .map(device => ({
          device: device.name || device.hostname || device.ipAddress,
          latency: Math.round(device.latency)
        }))
        .slice(0, 10); // Show top 10 devices
      
      setLatencyData(deviceLatency);
    } catch (error) {
      console.error("Error fetching latency data:", error);
    }
  };

  const fetchBandwidthData = async () => {
    try {
      // Generate mock bandwidth data for now (can be replaced with real monitoring logs)
      const currentHour = new Date().getHours();
      const hours = [];
      for (let i = Math.max(0, currentHour - 7); i <= currentHour; i++) {
        hours.push({
          time: `${i.toString().padStart(2, '0')}:00`,
          usage: Math.floor(Math.random() * 80) + 20 // Random usage between 20-100 Mbps
        });
      }
      setBandwidthData(hours);
    } catch (error) {
      console.error("Error fetching bandwidth data:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await API.get("/devices/stats");
      setStats(res.data);
    } catch {
      // Use dummy data if API not ready
      setStats({
        totalDevices: 24,
        onlineDevices: 20,
        offlineDevices: 4,
        avgLatency: 18,
      });
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await API.get("/alerts");
      // Process alerts to match expected format
      const processedAlerts = res.data
        .slice(0, 5) // Show only 5 most recent alerts
        .map(alert => ({
          id: alert.id,
          message: alert.message,
          time: new Date(alert.createdAt).toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          }),
          type: alert.severity === 'CRITICAL' ? 'error' : 
                alert.severity === 'WARNING' ? 'warning' : 'success'
        }));
      setRecentAlerts(processedAlerts);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      // Use fallback data if API not ready
      setRecentAlerts([
        { id: 1, message: "Router A went offline", time: "10:32 AM", type: "error" },
        { id: 2, message: "Switch B high latency detected", time: "11:15 AM", type: "warning" },
        { id: 3, message: "Server C back online", time: "12:00 PM", type: "success" },
      ]);
    }
  };

  const fetchDeviceTypeData = async () => {
    try {
      const res = await API.get("/devices");
      const deviceTypes = res.data.reduce((acc, device) => {
        const type = device.type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      
      const typeData = Object.entries(deviceTypes).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
      
      setDeviceTypeData(typeData);
    } catch (error) {
      console.error("Error fetching device type data:", error);
      setDeviceTypeData([
        { name: "Router", value: 2 },
        { name: "Switch", value: 3 },
        { name: "Server", value: 4 },
        { name: "Access Point", value: 2 },
      ]);
    }
  };

  const fetchAlertSeverityData = async () => {
    try {
      const res = await API.get("/alerts");
      const severityCounts = res.data.reduce((acc, alert) => {
        const severity = alert.severity || 'UNKNOWN';
        acc[severity] = (acc[severity] || 0) + 1;
        return acc;
      }, {});
      
      const severityData = Object.entries(severityCounts).map(([name, value]) => ({
        name,
        value
      }));
      
      setAlertSeverityData(severityData);
    } catch (error) {
      console.error("Error fetching alert severity data:", error);
      setAlertSeverityData([
        { name: "CRITICAL", value: 2 },
        { name: "WARNING", value: 3 },
      ]);
    }
  };

  const fetchNetworkTrafficData = async () => {
    try {
      // Generate mock network traffic data for the last 7 days
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const trafficData = days.map(day => ({
        day,
        inbound: Math.floor(Math.random() * 500) + 100,
        outbound: Math.floor(Math.random() * 400) + 80,
      }));
      
      setNetworkTrafficData(trafficData);
    } catch (error) {
      console.error("Error fetching network traffic data:", error);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
        <p className="text-sm text-gray-500">Real-time network overview</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Devices"
          value={stats.totalDevices}
          icon={<MdDevices size={24} className="text-blue-600" />}
          color="bg-blue-100"
        />
        <StatCard
          title="Online Devices"
          value={stats.onlineDevices}
          icon={<MdCheckCircle size={24} className="text-green-600" />}
          color="bg-green-100"
        />
        <StatCard
          title="Offline Devices"
          value={stats.offlineDevices}
          icon={<MdError size={24} className="text-red-600" />}
          color="bg-red-100"
        />
        <StatCard
          title="Avg Latency (ms)"
          value={`${stats.avgLatency}ms`}
          icon={<MdSpeed size={24} className="text-yellow-600" />}
          color="bg-yellow-100"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Bandwidth Chart */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-md font-semibold text-gray-700 mb-4">
            Bandwidth Usage (Today)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={bandwidthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="usage"
                stroke="#2563eb"
                strokeWidth={2}
                name="Usage (Mbps)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Latency Chart */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-md font-semibold text-gray-700 mb-4">
            Device Latency (ms)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={latencyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="device" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="latency" fill="#16a34a" name="Latency (ms)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Device Type Distribution */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-md font-semibold text-gray-700 mb-4">
            Device Types
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={deviceTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {deviceTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={['#2563eb', '#16a34a', '#f59e0b', '#ef4444'][index % 4]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Alert Severity Distribution */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-md font-semibold text-gray-700 mb-4">
            Alert Severity
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={alertSeverityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {alertSeverityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.name === 'CRITICAL' ? '#ef4444' : '#f59e0b'} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Network Traffic */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-md font-semibold text-gray-700 mb-4">
            Network Traffic (7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={networkTrafficData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="inbound"
                stackId="1"
                stroke="#2563eb"
                fill="#2563eb"
                fillOpacity={0.6}
                name="Inbound (GB)"
              />
              <Area
                type="monotone"
                dataKey="outbound"
                stackId="1"
                stroke="#16a34a"
                fill="#16a34a"
                fillOpacity={0.6}
                name="Outbound (GB)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="bg-white rounded-xl shadow p-5">
        <h3 className="text-md font-semibold text-gray-700 mb-4">
          Recent Alerts
        </h3>
        <div className="space-y-3">
          {recentAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm ${
                alert.type === "error"
                  ? "bg-red-50 text-red-700"
                  : alert.type === "warning"
                  ? "bg-yellow-50 text-yellow-700"
                  : "bg-green-50 text-green-700"
              }`}
            >
              <span>{alert.message}</span>
              <span className="text-xs opacity-70">{alert.time}</span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
